const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const formatMessage = require('format-message');
const axios = require('axios');
const codeGeneration = require('../codeGeneration');

let setupCode = '';
let defineCode = '';
let loopCode = '';
let bodyCode = '';
const initializedPins = new Set(); // To track initialized pins
const definedFunctions = new Set();
const definedVariable = new Set();

class Scratch3StemActuatorsBlocks {
    constructor(runtime) {
        this.runtime = runtime;
        this.lastThread = null;

        if (runtime) {
            runtime.on('PROJECT_RUN_START', () => {
                codeGeneration.onRunStart();
            });
            runtime.on('PROJECT_RUN_STOP', () => {
                codeGeneration.onRunStop();
            });
        }
    }

    _checkNewThread(util) {
        const thread = util && util.thread;
        if (thread && this.lastThread !== thread) {
            this.lastThread = thread;
            initializedPins.clear();
            definedFunctions.clear();
            definedVariable.clear();
        }
    }

    getInfo() {
        return {
            id: 'stemactuators',
            color1: '#cd950c',
            name: formatMessage({
                id: 'stemactuators.categoryName',
                default: 'Stem Actuators',
                description: 'Stem Actuators extension category'
            }),
            blocks: [
                {
                    opcode: 'contMotor',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'stemactuators.defMotor',
                        default: 'Connect motor [motor]',
                        description: 'connect motor'
                    }),
                    arguments: {
                        motor: {
                            type: ArgumentType.STRING,
                            menu: 'motorMenu',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'rotateMotor',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'stemactuators.rotateMotor',
                        default: 'Move [direction]',
                        description: 'move robot'
                    }),
                    arguments: {
                        direction: {
                            type: ArgumentType.STRING,
                            menu: 'directionMenu',
                            defaultValue: 'forward'
                        }
                    }
                },
                {
                    opcode: 'atchServo',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'stemactuators.atchServo',
                        default: 'Attach the servo (Pin 26)',
                        description: 'connect servo'
                    })
                },
                {
                    opcode: 'rotateServo',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'stemactuators.rotateServo',
                        default: 'rotate servo motor by [degree] degree',
                        description: 'rotate motor'
                    }),
                    arguments: {
                        degree: {
                            type: ArgumentType.ANGLE,
                            defaultValue: '0'
                        }
                    }
                }
            ],
            menus: {
                motorMenu: {
                    items: [
                        { text: '1 (Pin 16, 17, ENA 4)', value: '1' },
                        { text: '2 (Pin 18, 19, ENB 5)', value: '2' }
                    ]
                },
                directionMenu: {
                    items: ['forward', 'backward', 'left', 'right', 'stop']
                }
            }
        };
    }

    contMotor(args, util) {
        this._checkNewThread(util);
        const motor = args.motor;
        let pinno1 = '';
        let pinno2 = '';
        if (motor === '1') {
            pinno1 = '16';
            pinno2 = '17';
            defineCode += `#define m1a ${pinno1}\n#define m1b ${pinno2}\n`;
            setupCode += `pinMode(m1a, OUTPUT);\npinMode(m1b, OUTPUT);\n`;
        } else if (motor === '2') {
            pinno1 = '18';
            pinno2 = '19';
            defineCode += `#define m2a ${pinno1}\n#define m2b ${pinno2}\n`;
            setupCode += `pinMode(m2a, OUTPUT);\npinMode(m2b, OUTPUT);\n`;
        }

        defineCode += `#define e1 4\n#define e2 5\n`;
        setupCode += `pinMode(e1, OUTPUT);\npinMode(e2, OUTPUT);\n`;

        this.accumulateCode(util);
        this.resetCode();
    }

    rotateMotor(args, util) {
        this._checkNewThread(util);
        const direction = args.direction;

        // Ensure motor and enable pins are defined so movement functions always compile
        defineCode += `#define m1a 16\n#define m1b 17\n#define m2a 18\n#define m2b 19\n#define e1 4\n#define e2 5\n`;
        setupCode += `pinMode(m1a, OUTPUT);\npinMode(m1b, OUTPUT);\npinMode(m2a, OUTPUT);\npinMode(m2b, OUTPUT);\npinMode(e1, OUTPUT);\npinMode(e2, OUTPUT);\n`;

        const functionName = `move_${direction}`;

        if (direction === 'forward') {
            bodyCode += `\nvoid move_forward() {\n    digitalWrite(m1a, HIGH);\n    digitalWrite(m1b, LOW);\n    digitalWrite(m2a, HIGH);\n    digitalWrite(m2b, LOW);\n    analogWrite(e1, 200);\n    analogWrite(e2, 200);\n}\n`;
        } else if (direction === 'backward') {
            bodyCode += `\nvoid move_backward() {\n    digitalWrite(m1a, LOW);\n    digitalWrite(m1b, HIGH);\n    digitalWrite(m2a, LOW);\n    digitalWrite(m2b, HIGH);\n    analogWrite(e1, 200);\n    analogWrite(e2, 200);\n}\n`;
        } else if (direction === 'left') {
            bodyCode += `\nvoid move_left() {\n    digitalWrite(m1a, HIGH);\n    digitalWrite(m1b, LOW);\n    digitalWrite(m2a, LOW);\n    digitalWrite(m2b, HIGH);\n    analogWrite(e1, 200);\n    analogWrite(e2, 200);\n}\n`;
        } else if (direction === 'right') {
            bodyCode += `\nvoid move_right() {\n    digitalWrite(m1a, LOW);\n    digitalWrite(m1b, HIGH);\n    digitalWrite(m2a, HIGH);\n    digitalWrite(m2b, LOW);\n    analogWrite(e1, 200);\n    analogWrite(e2, 200);\n}\n`;
        } else if (direction === 'stop') {
            bodyCode += `\nvoid move_stop() {\n    digitalWrite(m1a, LOW);\n    digitalWrite(m1b, LOW);\n    digitalWrite(m2a, LOW);\n    digitalWrite(m2b, LOW);\n    analogWrite(e1, 200);\n    analogWrite(e2, 200);\n}\n`;
        }

        const codeToAdd = `${functionName}();\n`;

        if (localStorage.getItem('codeContext') === 'setup') {
            setupCode += codeToAdd;
        } else {
            loopCode += codeToAdd;
        }

        this.accumulateCode(util);
        this.resetCode();
    }

    atchServo(args, util) {
        this._checkNewThread(util);
        const pin = '26'; // Predefined pin 26
        defineCode += `#include <ESP32Servo.h>\nServo myServo;\n`;
        setupCode += `myServo.setPeriodHertz(50);\nmyServo.attach(${pin}, 500, 2400);\n`;
        this.accumulateCode(util);
        this.resetCode();
    }

    rotateServo(args, util) {
        this._checkNewThread(util);
        const degree = args.degree;
        const MIN_ANGLE = 0;
        const MAX_ANGLE = 180;
        if (degree < MIN_ANGLE || degree > MAX_ANGLE) {
            return `Enter the angle between ${MIN_ANGLE} and ${MAX_ANGLE}`;
        }
        const codeToAdd = `myServo.write(${degree});\n`;

        if (localStorage.getItem('codeContext') === 'setup') {
            setupCode += codeToAdd;
        } else {
            loopCode += codeToAdd;
        }

        this.accumulateCode(util);
        this.resetCode();
    }

    accumulateCode(util) {
        codeGeneration.accumulateCode(defineCode, bodyCode, setupCode, loopCode, util);
    }

    resetCode() {
        setupCode = '';
        loopCode = '';
        defineCode = '';
        bodyCode = '';
    }

    full_reset() {
        setupCode = '';
        loopCode = '';
        defineCode = '';
        bodyCode = '';
        initializedPins.clear();
        definedFunctions.clear();
        definedVariable.clear();
    }
}

module.exports = Scratch3StemActuatorsBlocks;
